import { useAdminCheck, useStytchLogout } from '@platform/auth';
import AuthDevPanel from '@platform/auth/dev/AuthDevPanel';
import type { User } from '@shared/types';
import { BottomBar, SideBar } from '@shared/ui';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { EnvironmentBar } from '../features/admin/components/EnvironmentBar';
import { analytics } from '../services/analytics';
import type { RootState } from '../store/store';
import AdminBreadcrumbs from './navigation/AdminBreadcrumbs';
import AdminNav from './navigation/AdminNav';
import AdminSearchPalette from './navigation/AdminSearchPalette';
import { BreadcrumbOverridesProvider } from './navigation/BreadcrumbOverridesContext';
import NonAdminBreadcrumbs from './navigation/NonAdminBreadcrumbs';
import UserNav from './navigation/UserNav';
import { useSidebarExpanded } from './navigation/useSidebarExpanded';
import { defaultShellHomeRoute, resolveLayoutShellMode } from './shellMode';

type LayoutShellProps = {
  children: React.ReactNode;
  centerElement?: React.ReactNode;
  leftSidebar?: React.ReactNode;
  leftSidebarWidth?: number;
  leftSidebarCollapsible?: boolean;
  rightSidebar?: React.ReactNode;
  rightSidebarWidth?: number;
  rightSidebarCollapsible?: boolean;
  fullWidth?: boolean;
  /**
   * Overrides the shell's sage gradient for this route only. For a page that fills its own
   * background edge-to-edge (a dashboard, a full-bleed tool), the gradient only ever shows
   * through the shell's own gutters, so this exists to make that override explicit rather
   * than asking every such page to fight the gradient with its own overlay.
   */
  contentBackground?: string;
  /** Skips the breadcrumb row for this route — for a page whose own nav already says where it is. */
  hideBreadcrumbs?: boolean;
  /**
   * Drops the fullWidth content wrapper's padding and stretches it to the route's full
   * available height, so a page built to fill its own frame edge-to-edge (its own rail, its
   * own scroll regions) actually reaches every edge instead of sitting in a gutter that
   * exposes the shell background around it.
   */
  fillHeight?: boolean;
};

const LayoutShell: React.FC<LayoutShellProps> = ({
  children,
  centerElement,
  leftSidebar,
  leftSidebarWidth = 420,
  leftSidebarCollapsible: _leftSidebarCollapsible = false,
  rightSidebar,
  rightSidebarWidth = 420,
  rightSidebarCollapsible: _rightSidebarCollapsible = false,
  fullWidth = false,
  contentBackground,
  hideBreadcrumbs = false,
  fillHeight = false,
}) => {
  const authUser = useSelector((s: RootState) => s.auth.user);
  const { isAdmin, permissions, roles } = useAdminCheck();
  const location = useLocation();
  const shellMode = resolveLayoutShellMode(location.pathname, authUser);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const showAdminNav = isAdmin && isAdminRoute;
  const avatarMenuMode = shellMode === 'member' ? 'sign-out-only' : 'default';

  const { isExpanded: isSideBarExpanded, toggle: toggleSideBarExpanded } = useSidebarExpanded();
  const [isAdminSearchOpen, setAdminSearchOpen] = useState(false);
  const openAdminSearch = useCallback(() => setAdminSearchOpen(true), []);
  const closeAdminSearch = useCallback(() => setAdminSearchOpen(false), []);
  const [currentRightWidth, setCurrentRightWidth] = useState(rightSidebarWidth);

  const isMemberShell = shellMode === 'member';

  const menu = showAdminNav ? (
    <AdminNav
      permissions={permissions}
      roles={roles}
      isExpanded={isSideBarExpanded}
      onOpenSearch={openAdminSearch}
    />
  ) : (
    <UserNav
      isExpanded={isSideBarExpanded}
      shellMode={isMemberShell ? 'member' : 'coach'}
      lightTheme={isMemberShell}
    />
  );

  const user: User | null = authUser
    ? {
        id: authUser.id,
        firstName: (authUser as any).firstName ?? authUser.email?.split('@')[0] ?? 'User',
        lastName: (authUser as any).lastName ?? '',
        email: authUser.email,
        avatarUrl: (authUser as any).avatarUrl ?? undefined,
        fullName:
          `${(authUser as any).firstName ?? ''} ${(authUser as any).lastName ?? ''}`.trim() ||
          authUser.email,
      }
    : null;

  const navigate = useNavigate();
  const { signOut } = useStytchLogout();
  const onSignOut = async () => {
    analytics.track('Signed out', {
      route: location.pathname,
      screen: location.pathname,
    });
    await signOut();
    navigate('/login');
  };

  const gradientStyle = contentBackground
    ? { background: contentBackground }
    : isMemberShell
      ? { background: '#FEFBF7' }
      : { background: 'linear-gradient(135deg, #D7E1DB 0%, #BDCCBE 50%, #637A6F 100%)' };
  const sideBarWidthPx = isSideBarExpanded ? 226 : 72;
  const breadcrumbNav =
    shellMode === 'admin' ? <AdminBreadcrumbs /> : <NonAdminBreadcrumbs viewerMode={shellMode} />;

  // Inject onWidthChange so SidebarChat can keep marginRight in sync
  const rightSidebarWithCallback = rightSidebar
    ? React.cloneElement(rightSidebar as React.ReactElement<any>, {
        onWidthChange: setCurrentRightWidth,
      })
    : null;

  return (
    <BreadcrumbOverridesProvider>
      <div className="absolute inset-0 flex flex-col overflow-hidden">
        <EnvironmentBar />
        <div className="flex flex-1 overflow-auto" style={gradientStyle}>
          <SideBar
            hideSideBar={false}
            isExpanded={isSideBarExpanded}
            isOnHomepage={false}
            menu={menu}
            user={user}
            onAccountSettings={() => {}}
            onFeedBack={() => {}}
            onSignOut={onSignOut}
            onToggleExpanded={toggleSideBarExpanded}
            homeRoute={defaultShellHomeRoute(shellMode)}
            avatarMenuMode={avatarMenuMode}
            isAdmin={isAdmin}
            isAdminRoute={isAdminRoute}
            lightTheme={isMemberShell}
          />
          <main
            className={clsx(
              'mb-16 flex flex-1 flex-col overflow-x-hidden sm:mb-0',
              isSideBarExpanded ? 'sm:ml-[226px]' : 'sm:ml-18',
              fillHeight && 'min-h-0'
            )}
            style={{
              marginLeft: leftSidebar ? `${leftSidebarWidth + sideBarWidthPx}px` : undefined,
              marginRight: rightSidebar ? `${currentRightWidth}px` : undefined,
            }}
          >
            {centerElement && (
              <div className="sticky top-0 z-10 flex items-center justify-center border-b border-neutral-200 bg-white/90 px-3 py-2 backdrop-blur">
                {centerElement}
              </div>
            )}
            {!hideBreadcrumbs && <div className="empty:hidden px-6 pt-4">{breadcrumbNav}</div>}
            <div
              className={
                fullWidth
                  ? clsx('w-full', fillHeight ? 'flex min-h-0 flex-1 flex-col' : 'px-6 py-4')
                  : 'container mx-auto max-w-screen-lg p-4'
              }
            >
              {children}
            </div>
          </main>
          {leftSidebar && (
            <aside
              className={clsx(
                'fixed top-0 bottom-0 hidden sm:block',
                isSideBarExpanded ? 'left-[226px]' : 'left-18'
              )}
              style={{ width: `${leftSidebarWidth}px` }}
            >
              {leftSidebar}
            </aside>
          )}
          {rightSidebarWithCallback && (
            <aside
              className="fixed right-0 top-0 bottom-0 hidden sm:block"
              style={{ width: `${currentRightWidth}px`, overflow: 'visible' }}
            >
              {rightSidebarWithCallback}
            </aside>
          )}
          <BottomBar
            user={user ?? undefined}
            menu={[]}
            moreMenu={[]}
            onAccountSettings={() => {}}
            onFeedBack={() => {}}
            onSignOut={onSignOut}
          />
          {showAdminNav && (
            <AdminSearchPalette
              isOpen={isAdminSearchOpen}
              onClose={closeAdminSearch}
              onOpen={openAdminSearch}
              permissions={permissions}
              roles={roles}
            />
          )}
          <AuthDevPanel />
        </div>
      </div>
    </BreadcrumbOverridesProvider>
  );
};

export default LayoutShell;
